import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, Box, DollarSign, AlertTriangle, Wrench, PlusCircle, Scan, Printer, Layers } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const STATUS_LABELS = {
  in_stock: 'In Stock', assigned: 'Assigned', maintenance: 'Maintenance',
  retired: 'Retired', disposed: 'Disposed', lost: 'Lost',
};
const STATUS_COLORS = {
  in_stock: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  assigned: 'bg-sky-50 text-sky-700 border-sky-200',
  maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
  retired: 'bg-slate-50 text-slate-600 border-slate-200',
  disposed: 'bg-slate-50 text-slate-500 border-slate-200',
  lost: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function AssetsWorkspacePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/assets/analytics`, { headers: authHeader() })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
  if (!data) return <div className="p-12 text-center text-[#64748B]">Could not load analytics</div>;

  return (
    <div className="p-6 space-y-6" data-testid="assets-workspace">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">Asset Management</h1>
          <p className="text-sm text-[#64748B] mt-1">Track every laptop, tablet, medical device, and vehicle across your organization.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/assets/scan"><Button variant="outline" size="sm" data-testid="goto-scan"><Scan className="w-4 h-4 mr-2" /> Scan</Button></Link>
          <Link to="/assets/labels"><Button variant="outline" size="sm" data-testid="goto-labels"><Printer className="w-4 h-4 mr-2" /> Labels</Button></Link>
          <Link to="/assets/new"><Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="goto-new"><PlusCircle className="w-4 h-4 mr-2" /> New asset</Button></Link>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total assets" value={data.total_assets} Icon={Box} accent="#0E7490" onClick={() => navigate('/assets/list')} testid="kpi-total" />
        <Kpi label="Total value" value={fmtMoney(data.total_value)} Icon={DollarSign} accent="#10B981" testid="kpi-value" />
        <Kpi label="Warranty alerts" value={(data.warranty_expiring_soon || 0) + (data.warranty_expired || 0)}
             Icon={AlertTriangle} accent="#F59E0B" testid="kpi-warranty" subtext={`${data.warranty_expiring_soon} soon · ${data.warranty_expired} expired`} />
        <Kpi label="Maintenance due" value={data.maintenance_due} Icon={Wrench} accent="#EF4444" testid="kpi-maint"
             onClick={() => navigate('/assets/list?status=maintenance')} />
      </div>

      {/* Two columns: by status + by category */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5 border-[#E2E8F0]">
          <h3 className="text-sm font-semibold text-[#0A2342] mb-3">By status</h3>
          <div className="space-y-2">
            {Object.entries(data.by_status).map(([s, n]) => {
              const pct = data.total_assets ? Math.round((n / data.total_assets) * 100) : 0;
              return (
                <div key={s} className="cursor-pointer hover:bg-[#F8FAFC] -mx-2 px-2 py-1 rounded"
                     onClick={() => navigate(`/assets/list?status=${s}`)} data-testid={`status-${s}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-1.5 py-0.5 rounded border ${STATUS_COLORS[s]}`}>{STATUS_LABELS[s]}</span>
                    <span className="text-[#0A2342] font-mono">{n} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[#F1F5F9] rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-[#0E7490]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5 border-[#E2E8F0]">
          <h3 className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-[#0E7490]" /> By category</h3>
          <div className="space-y-2">
            {(data.by_category || []).map(c => {
              const pct = data.total_assets ? Math.round((c.count / data.total_assets) * 100) : 0;
              return (
                <div key={c.category_id} className="cursor-pointer hover:bg-[#F8FAFC] -mx-2 px-2 py-1 rounded"
                     onClick={() => navigate(`/assets/list?category_id=${c.category_id}`)} data-testid={`cat-${c.category_id}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#0A2342]">{c.category_name}</span>
                    <span className="text-[#64748B] font-mono">{c.count} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[#F1F5F9] rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-[#10B981]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, Icon, accent, subtext, onClick, testid }) {
  return (
    <Card className={`p-4 border-[#E2E8F0] ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow`}
          onClick={onClick} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-[#64748B]">{label}</p>
          <p className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</p>
          {subtext && <p className="text-[10px] text-[#94A3B8] mt-0.5">{subtext}</p>}
        </div>
        <Icon className="w-8 h-8 opacity-60" style={{ color: accent }} />
      </div>
    </Card>
  );
}
